import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, PencilSimple, Buildings, MapPin, House as HouseIcon, Car, CurrencyDollar } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;
const TYPES = [
  { value: 'land', label: 'Land', icon: MapPin },
  { value: 'house', label: 'House / Building', icon: HouseIcon },
  { value: 'vehicle', label: 'Vehicle', icon: Car },
  { value: 'gold', label: 'Gold / Jewelry', icon: CurrencyDollar },
  { value: 'other', label: 'Other Asset', icon: Buildings },
];

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', property_type: 'land', value: '', purchase_date: '', description: '', location: '' });

  const load = () => { api.get('/properties').then(r => setProperties(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, value: parseFloat(form.value) || 0 };
    if (editId) {
      await api.put(`/properties/${editId}`, payload);
    } else {
      await api.post('/properties', payload);
    }
    setDialogOpen(false);
    setEditId(null);
    setForm({ name: '', property_type: 'land', value: '', purchase_date: '', description: '', location: '' });
    load();
  };

  const handleEdit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, property_type: p.property_type, value: String(p.value), purchase_date: p.purchase_date || '', description: p.description || '', location: p.location || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => { await api.delete(`/properties/${id}`); load(); };
  const totalValue = properties.reduce((s, p) => s + p.value, 0);

  const typeGroup = {};
  properties.forEach(p => {
    if (!typeGroup[p.property_type]) typeGroup[p.property_type] = [];
    typeGroup[p.property_type].push(p);
  });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="properties-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Properties & Assets</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Track your land, house, vehicles, gold, and other assets</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditId(null); setForm({ name: '', property_type: 'land', value: '', purchase_date: '', description: '', location: '' }); } }}>
          <DialogTrigger asChild>
            <button data-testid="add-property-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm"><Plus size={18} weight="bold" /> Add Property</button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
            <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{editId ? 'Edit Property' : 'Add Property'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Property Type</label>
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger className="w-full border-[#E8E6E1] rounded-lg" data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Name / Title</label>
                <input data-testid="property-name-input" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="2 Aana Land in Kathmandu" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Current Value (Rs.)</label>
                  <input data-testid="property-value-input" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="5000000" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Purchase Date</label>
                  <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Location</label>
                <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="Kathmandu, Nepal" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Description</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="Residential land near Ring Road" />
              </div>
              <button data-testid="submit-property-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">{editId ? 'Update Property' : 'Add Property'}</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Total Value */}
      <div className="bg-gradient-to-br from-[#1E3F2D] to-[#265C42] rounded-2xl shadow-sm p-6 text-white" data-testid="total-property-value">
        <p className="text-sm font-bold uppercase tracking-[0.2em] opacity-70">Total Property Value</p>
        <p className="text-3xl font-bold font-mono-numbers mt-1">{fmt(totalValue)}</p>
        <p className="text-sm opacity-60 mt-1">{properties.length} asset{properties.length !== 1 ? 's' : ''} tracked</p>
      </div>

      {/* Properties by type */}
      {Object.keys(typeGroup).length > 0 ? (
        Object.entries(typeGroup).map(([type, items]) => {
          const typeInfo = TYPES.find(t => t.value === type) || TYPES[4];
          const TypeIcon = typeInfo.icon;
          const typeTotal = items.reduce((s, p) => s + p.value, 0);
          return (
            <div key={type}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TypeIcon size={16} weight="duotone" className="text-[#1E3F2D]" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#5C5A56]">{typeInfo.label}</h3>
                </div>
                <span className="text-sm font-semibold font-mono-numbers text-[#1E3F2D]">{fmt(typeTotal)}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`properties-${type}`}>
                {items.map(p => (
                  <div key={p.id} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 hover:shadow-md transition-all duration-300" data-testid={`property-${p.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-base font-semibold text-[#1C1B1A]">{p.name}</p>
                        {p.location && <p className="text-xs text-[#5C5A56] flex items-center gap-1"><MapPin size={12} /> {p.location}</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEdit(p)} className="text-[#5C5A56] hover:text-[#1E3F2D] transition-colors p-1" data-testid={`edit-${p.id}`}><PencilSimple size={14} /></button>
                        <button onClick={() => handleDelete(p.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1"><Trash size={14} /></button>
                      </div>
                    </div>
                    <p className="text-xl font-bold font-mono-numbers text-[#1E3F2D]">{fmt(p.value)}</p>
                    {p.description && <p className="text-xs text-[#5C5A56] mt-1">{p.description}</p>}
                    {p.purchase_date && <p className="text-[10px] text-[#5C5A56] mt-1">Purchased: {p.purchase_date}</p>}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      ) : !loading && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">No properties added yet. Track your land, house, vehicles, and other assets!</div>
      )}
    </div>
  );
}
